export interface ParsedVideo {
    platform: "youtube" | "dailymotion" | "vimeo" | "tiktok" | "facebook" | "instagram";
    videoId: string;
    isShort: boolean;
}

const PATTERNS: Array<{
    platform: ParsedVideo["platform"];
    regex: RegExp;
    idGroup: number;
    isShort?: boolean | ((match: RegExpMatchArray) => boolean);
}> = [
    // YouTube Shorts
    {
        platform: "youtube",
        regex: /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]+)/,
        idGroup: 1,
        isShort: true,
    },
    // YouTube watch
    {
        platform: "youtube",
        regex: /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/,
        idGroup: 1,
        isShort: false,
    },
    // Dailymotion
    {
        platform: "dailymotion",
        regex: /dailymotion\.com\/video\/([a-zA-Z0-9]+)/,
        idGroup: 1,
        isShort: false,
    },
    // Vimeo
    {
        platform: "vimeo",
        regex: /vimeo\.com\/(\d+)/,
        idGroup: 1,
        isShort: false,
    },
    // TikTok
    {
        platform: "tiktok",
        regex: /tiktok\.com\/@[^/]+\/video\/(\d+)/,
        idGroup: 1,
        isShort: true,
    },
    // Facebook watch
    {
        platform: "facebook",
        regex: /(?:facebook\.com\/watch\/?\?v=|fb\.watch\/)([a-zA-Z0-9_-]+)/,
        idGroup: 1,
        isShort: false,
    },
    // Instagram Reel
    {
        platform: "instagram",
        regex: /instagram\.com\/reel\/([a-zA-Z0-9_-]+)/,
        idGroup: 1,
        isShort: true,
    },
    // Instagram Post
    {
        platform: "instagram",
        regex: /instagram\.com\/p\/([a-zA-Z0-9_-]+)/,
        idGroup: 1,
        isShort: false,
    },
];

/**
 * Parse a video platform URL to extract platform, video ID, and whether it's a short.
 * Returns null if the URL doesn't match any known pattern.
 */
export function parseVideoUrl(url: string): ParsedVideo | null {
    for (const pattern of PATTERNS) {
        const match = url.match(pattern.regex);
        if (match) {
            return {
                platform: pattern.platform,
                videoId: match[pattern.idGroup],
                isShort:
                    typeof pattern.isShort === "function" ? pattern.isShort(match) : (pattern.isShort ?? false),
            };
        }
    }
    return null;
}

/**
 * Generate an embeddable iframe URL for a given platform and video ID.
 * Returns null for platforms that require script-based embedding (Instagram, TikTok).
 */
export function generateEmbedUrl(platform: string, videoId: string): string | null {
    switch (platform) {
        case "youtube":
            return `https://www.youtube.com/embed/${videoId}`;
        case "dailymotion":
            return `https://www.dailymotion.com/embed/video/${videoId}`;
        case "vimeo":
            return `https://player.vimeo.com/video/${videoId}`;
        case "facebook":
            return `https://www.facebook.com/plugins/video.php?href=https://www.facebook.com/watch/?v=${videoId}`;
        // Instagram and TikTok require script-based embedding — use raw embedCode
        case "instagram":
        case "tiktok":
            return null;
        default:
            return null;
    }
}
